/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createSlice } from '@reduxjs/toolkit';

import { ApiStatus } from '../../../ts';
import {
  CUSTOM_SERVICE_SEARCH_AUTH_TYPES,
  SEARCH_PROVIDER_TYPES
} from '../../../ts/skill';
import type { CustomServiceSlice } from '../../../ts/state';
import {
  CONFIDENCE_THRESHOLD_VALUE,
  RESPONSE_LENGTH_VALUE
} from '../../discovery/util/constants';
import { customServiceAdapter } from './adapter';

const initialState: CustomServiceSlice = customServiceAdapter.getInitialState({
  draft: null,
  isDirty: false,
  isEditing: false,
  showDetailConfig: false,
  customServiceAuthType: CUSTOM_SERVICE_SEARCH_AUTH_TYPES.BASIC,
  previouslySavedCustomServiceAuthType: null,
  selectedSearchType: SEARCH_PROVIDER_TYPES.SERVER_SIDE,
  isCustomServiceSetupValid: false,
  isCustomServiceConfigValid: false,
  createCustomService: false,
  createCustomServiceStatus: ApiStatus.IDLE,
  createCustomServiceError: null,
  skillId: null,
  filter: '',
  metadata: null
});

// Slice & Reducer
const slice = createSlice({
  name: 'customService',
  initialState,
  reducers: {
    resetCustomServiceState() {
      return initialState;
    },
    setCustomServiceUrl(state: CustomServiceSlice, action) {
      state.draft.search_settings.server_side_search.url = action.payload;
      state.isDirty = true;
    },
    setCustomServiceUser(state: CustomServiceSlice, action) {
      state.draft.search_settings.server_side_search.username = action.payload;
      state.isDirty = true;
    },
    clearCustomServiceUser(state: CustomServiceSlice) {
      delete state?.draft?.search_settings?.server_side_search?.username;
    },
    setCustomServicePass(state: CustomServiceSlice, action) {
      state.draft.search_settings.server_side_search.password = action.payload;
      state.isDirty = true;
    },
    clearCustomServicePass(state: CustomServiceSlice) {
      delete state?.draft?.search_settings?.server_side_search?.password;
    },
    setCustomServiceApiKey(state: CustomServiceSlice, action) {
      state.draft.search_settings.server_side_search.apikey = action.payload;
      state.isDirty = true;
    },
    clearCustomServiceApiKey(state: CustomServiceSlice) {
      delete state?.draft?.search_settings?.server_side_search?.apikey;
    },
    setCustomServiceAuthType(state: CustomServiceSlice, action) {
      state.customServiceAuthType = action.payload;
    },
    setCustomServiceAuthTypeDefault(state: CustomServiceSlice) {
      state.customServiceAuthType = CUSTOM_SERVICE_SEARCH_AUTH_TYPES.BASIC;
    },
    setPreviouslySavedCustomServiceAuthType(state: CustomServiceSlice, action) {
      state.previouslySavedCustomServiceAuthType = action.payload;
      state.customServiceAuthType = action.payload;
    },
    clearDiscoveryDetails(state: CustomServiceSlice) {
      delete state?.draft?.search_settings?.discovery;
    },
    setIsCustomServiceSetupValid(state: CustomServiceSlice, action) {
      state.isCustomServiceSetupValid = action.payload;
    },
    setIsCustomServiceConfigValid(state: CustomServiceSlice, action) {
      state.isCustomServiceConfigValid = action.payload;
    },
    setCustomServiceSearchSkillDraft(state: CustomServiceSlice, action) {
      const { skill, isDirty } = action.payload;
      state.draft = skill || null;
      state.isDirty = isDirty;
    },
    setIsDirty(state: CustomServiceSlice, action) {
      state.isDirty = action.payload;
    },
    resetIsDirty(state: CustomServiceSlice) {
      state.isDirty = false;
    },
    setIsEditing(state: CustomServiceSlice, action) {
      state.isEditing = action.payload;
    },
    // This reducer does not set isDirty to true because it's just updating defaults. We don't need to confuse tanya by
    // enabling the save button for the defaults, instead the defaults will only be saved if she makes an edit and then
    // chooses to save.
    setDraftConversationalSearchEnabledDefault(state: CustomServiceSlice) {
      state.draft.search_settings.conversational_search = {
        ...state.draft.search_settings.conversational_search,
        enabled: false
      };
    },
    setDraftConversationalSearchRetrievalConfidenceThresholdDefault(
      state: CustomServiceSlice
    ) {
      state.draft.search_settings.conversational_search.search_confidence = {
        ...state.draft.search_settings.conversational_search.search_confidence,
        pre_gen_threshold: CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN
      };
    },
    setDraftConversationalSearchConfidenceThresholdDefault(
      state: CustomServiceSlice
    ) {
      state.draft.search_settings.conversational_search.search_confidence = {
        ...state.draft.search_settings.conversational_search.search_confidence,
        threshold: CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN
      };
    },
    setDraftConversationalSearchVerbosityOptionDefault(
      state: CustomServiceSlice
    ) {
      state.draft.search_settings.conversational_search.response_length = {
        ...state.draft.search_settings.conversational_search.response_length,
        option: RESPONSE_LENGTH_VALUE.MODERATE
      };
    },
    setDraftConversationalSearchResponseConfidenceThresholdDefault(
      state: CustomServiceSlice
    ) {
      state.draft.search_settings.conversational_search.search_confidence = {
        ...state.draft.search_settings.conversational_search.search_confidence,
        post_gen_threshold: CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN
      };
    },
    // This reducer does not set isDirty to true because it's just updating defaults. We don't need to confuse tanya by
    // enabling the save button for the defaults, instead the defaults will only be saved if she makes an edit and then
    // chooses to save.
    setDraftCitationsTitleValueDefault(state: CustomServiceSlice, action) {
      state.draft.search_settings.conversational_search = {
        ...state.draft.search_settings.conversational_search,
        citations_title: action.payload
      };
    },
    setDraftMaxCitationsValueDefault(state: CustomServiceSlice, action) {
      state.draft.search_settings.conversational_search.citations = {
        ...state.draft.search_settings.conversational_search.citations,
        max_citations: action.payload
      };
    },
    setCustomServiceDraftIsConversationalSearchEnabled(
      state: CustomServiceSlice,
      action
    ) {
      state.draft.search_settings.conversational_search = {
        ...state.draft.search_settings.conversational_search,
        enabled: action.payload
      };
      state.isDirty = true;
    },
    setDraftConversationalSearchRetrievalConfidenceThreshold(
      state: CustomServiceSlice,
      action
    ) {
      state.draft.search_settings.conversational_search.search_confidence = {
        ...state.draft.search_settings.conversational_search.search_confidence,
        pre_gen_threshold: action.payload
      };
      state.isDirty = true;
    },
    setDraftConversationalSearchConfidenceThreshold(
      state: CustomServiceSlice,
      action
    ) {
      state.draft.search_settings.conversational_search.search_confidence = {
        ...state.draft.search_settings.conversational_search.search_confidence,
        threshold: action.payload
      };
      state.isDirty = true;
    },
    setDraftConversationalSearchResponseConfidenceThreshold(
      state: CustomServiceSlice,
      action
    ) {
      state.draft.search_settings.conversational_search.search_confidence = {
        ...state.draft.search_settings.conversational_search.search_confidence,
        post_gen_threshold: action.payload
      };
      state.isDirty = true;
    },
    setDraftConversationalSearchMultiTurn(state: CustomServiceSlice, action) {
      state.draft.search_settings.conversational_search.multi_turn = {
        ...state.draft.search_settings.conversational_search.multi_turn,
        enabled: action.payload
      };
      state.isDirty = true;
    },
    setDraftConversationalSearchMultiTurnDefault(state: CustomServiceSlice) {
      state.draft.search_settings.conversational_search.multi_turn = {
        ...state.draft.search_settings.conversational_search.multi_turn,
        enabled: false
      };
    },
    setDraftConversationalSearchVerbosityOption(
      state: CustomServiceSlice,
      action
    ) {
      state.draft.search_settings.conversational_search.response_length = {
        ...state.draft.search_settings.conversational_search.response_length,
        option: action.payload
      };
      state.isDirty = true;
    },
    setDraftCitationsTitleValue(state: CustomServiceSlice, action) {
      state.draft.search_settings.conversational_search = {
        ...state.draft.search_settings.conversational_search,
        citations_title: action.payload
      };
      state.isDirty = true;
    },
    setDraftMaxCitationsValue(state: CustomServiceSlice, action) {
      state.draft.search_settings.conversational_search.citations = {
        ...state.draft.search_settings.conversational_search.citations,
        max_citations: action.payload
      };
      state.isDirty = true;
    },
    setDraftSuccessMessage(state: CustomServiceSlice, action) {
      state.draft.search_settings.messages.success = action.payload;
      state.isDirty = true;
    },
    setDraftNoResultsMessage(state: CustomServiceSlice, action) {
      state.draft.search_settings.messages.no_result = action.payload;
      state.isDirty = true;
    },
    setDraftConnectivityMessage(state: CustomServiceSlice, action) {
      state.draft.search_settings.messages.error = action.payload;
      state.isDirty = true;
    },
    setCustomServiceFilterValue(state: CustomServiceSlice, action) {
      state.filter = action.payload;
      state.isDirty = true;
    },
    setCustomServiceMetadataValue(state: CustomServiceSlice, action) {
      state.metadata = action.payload;
      state.isDirty = true;
    },
    setSelectedSearchType(state: CustomServiceSlice, action) {
      state.selectedSearchType = action.payload;
    },
    initializeClientSideSearch(state: CustomServiceSlice) {
      state.draft.search_settings.client_side_search = {};
    },
    initializeServerSideSearch(state: CustomServiceSlice) {
      state.draft.search_settings.server_side_search = {
        url: '',
        username: '',
        password: '',
        apikey: ''
      };
    }
  }
});

export const {
  resetCustomServiceState,
  setCustomServiceUrl,
  setCustomServiceUser,
  clearCustomServiceUser,
  setCustomServicePass,
  clearCustomServicePass,
  setCustomServiceApiKey,
  clearCustomServiceApiKey,
  setCustomServiceAuthType,
  setCustomServiceAuthTypeDefault,
  setPreviouslySavedCustomServiceAuthType,
  clearDiscoveryDetails,
  setIsCustomServiceSetupValid,
  setIsCustomServiceConfigValid,
  setCustomServiceSearchSkillDraft,
  setIsDirty,
  resetIsDirty,
  setIsEditing,
  setDraftConversationalSearchEnabledDefault,
  setDraftConversationalSearchRetrievalConfidenceThresholdDefault,
  setDraftConversationalSearchConfidenceThresholdDefault,
  setDraftConversationalSearchVerbosityOptionDefault,
  setDraftConversationalSearchResponseConfidenceThresholdDefault,
  setDraftCitationsTitleValueDefault,
  setDraftMaxCitationsValueDefault,
  setCustomServiceDraftIsConversationalSearchEnabled,
  setDraftConversationalSearchRetrievalConfidenceThreshold,
  setDraftConversationalSearchConfidenceThreshold,
  setDraftConversationalSearchVerbosityOption,
  setDraftConversationalSearchResponseConfidenceThreshold,
  setDraftCitationsTitleValue,
  setDraftMaxCitationsValue,
  setDraftSuccessMessage,
  setDraftNoResultsMessage,
  setDraftConnectivityMessage,
  setCustomServiceFilterValue,
  setCustomServiceMetadataValue,
  setSelectedSearchType,
  initializeServerSideSearch,
  initializeClientSideSearch,
  setDraftConversationalSearchMultiTurn,
  setDraftConversationalSearchMultiTurnDefault
} = slice.actions;

export default slice.reducer;
